/* eslint-disable @typescript-eslint/no-unsafe-call */

import React, { useCallback, useState } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import type { EmotionJSX } from '@emotion/react/types/jsx-namespace';

import {
    Typography,
    IconButton,
    CardActions,
    Card,
    DialogContent,
    DialogContentText,
    DialogActions,
    Box,
} from '@mui/material';

import { useTranslation } from 'next-i18next';

import ImagePartPostModal from 'src/components/news/posts/ImagePartPostModal';
import Button from 'src/components/ui/Button';
import Dialog from 'src/components/ui/Dialog';
import SelectNewsPostModal from 'src/components/news/SelectNewsPostModal';
import SelectAdModal from 'src/components/news/SelectAdModal';
import DialogTitle from 'src/components/ui/DialogTitle';
import Menu, { MenuItem } from 'src/components/ui/Menu';
import TinyPreviewCard from 'src/components/ui/TinyPreviewCard/TinyPreviewCard';
import MentionTextField from 'src/components/ui/MentionTextField';
import { Error } from 'src/components/ui/TextField';
import Icon from 'src/components/ui/Icon';
import Loader from 'src/components/ui/Loader';
import PostFileInput from 'src/components/news/posts/PostFileInput';

import { commonMultilineMaxLength } from 'src/constants/misc';
import { colours } from 'src/constants/colours';
import { breakpoints } from 'src/constants/breakpoints';

import { logEvent } from 'src/utils/firebase';
import { isContentTypeManageable } from 'src/utils/companyUtils';
import { AnalyticsEvent } from 'src/utils/analytics';

import useAlerts from 'src/hooks/useAlerts';
import useModalOpenState from 'src/hooks/useModalOpenState';
import useLoginModal from 'src/hooks/useLoginModal';
import useUser from 'src/hooks/useUser';

import {
    useUpdatePostMutation,
    useCreatePostMutation,
    useNewsPostQuery,
    useAdLightQuery,
    useProjectQuery,
} from 'src/hooks/__generated__/queries';
import type {
    PostsQuery,
    HybridMedium,
    Medium,
    Ad,
    Project,
    Company,
    NewsCategory,
} from 'src/types/__generated__/graphql';
import { ContentType } from 'src/types/__generated__/graphql';
import SelectProjectModal from 'src/components/news/SelectProjectModal';
import formatPrice from 'src/utils/formatPrice';
import { isMessageWithMentionsLengthValid } from 'src/utils/mentionsUtils';
import { styledComponent } from 'src/utils/styled';

const StyledDialog = styledComponent(Dialog)`
    .MuiDialog-paper {
        width: 30rem;
    }
    .MuiDialogTitle-root {
        padding: 1.25rem 1.5rem 0.75rem 1.5rem;
    }
    .MuiDialogActions-root {
        padding: 0.5rem 1.5rem 1.5rem 1.5rem;
    }
    @media (max-width: ${breakpoints.tabletPortrait - 1}px) {
        .MuiPaper-root {
            max-width: 100%;
            padding: 0;
            width: 100%;
            height: 100%;
            max-height: 100%;
            margin: 0;
            border-radius: 0;
            justify-content: center;
        }
    }
`;

const StyledBox = styledComponent(Box)`
    > :first-child {
        margin-right: 1rem;
    }
    display: flex;
    width: 100%;
    flex-direction: column;
`;

const StyledIconCloseButton = styledComponent(IconButton)`
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
`;

const StyledTinyPreviewCard = styledComponent(Card)`
    position: relative;
    box-shadow: 0rem 0.25rem 2rem -0.5rem ${colours.blackOpacity.O16};
`;

const StyledIconButton = styledComponent(IconButton)`
    border: 0.0625rem solid ${colours.iconButtonBorder};
    border-radius: 2.5rem;
    height: 2.5rem;
    width: 2.5rem;
    margin-left: 0.5rem;
    .MuiIconButton-label {
        width: fit-content;
    }
    svg {
        cursor: pointer !important;
    }
`;

type CustionMedium = Medium & { file?: File; src?: string };

interface FormValues {
    id: string;
    message?: string;
    media: CustionMedium[];
}

interface Props {
    onClose: () => void;
    open: boolean;
    post?: ArrayElement<PostsQuery['posts']>;
    trigerOpenFolderOnOpen?: MediaTypes;
}

const EditPostModal: React.FC<Props> = ({ post, onClose, open, trigerOpenFolderOnOpen }) => {
    const { t, i18n } = useTranslation(['news', 'common']);
    const { addAlert } = useAlerts();
    const { createAuthenticatedHandler } = useLoginModal();
    const currentUser = useUser();

    const { props: selectAdModalProps, open: openSelectAdModal } = useModalOpenState();
    const { props: selectProjectModalProps, open: openSelectProjectModal } = useModalOpenState();
    const { props: selectNewsPostModalProps, open: openSelectNewsPostModal } = useModalOpenState();

    const [selectedNewsPostId, setSelectedNewsPostId] = useState<string | null>(post?.newsPost?.id as string | null);
    const [selectedAdId, setSelectedAdId] = useState<string | null>(post?.ad?.id as string | null);
    const [selectedProjectId, setSelectedProjectId] = useState<Project['id'] | null>(
        post?.project?.id as Project['id'] | null,
    );

    const { data: newsPost } = useNewsPostQuery({
        variables: {
            newsPostId: String(selectedNewsPostId),
        },
        skip: !selectedNewsPostId,
    });

    const { data: ad } = useAdLightQuery({
        variables: {
            id: String(selectedAdId),
        },
        skip: !selectedAdId,
    });

    const { data: project } = useProjectQuery({
        variables: {
            projectId: String(selectedProjectId),
        },
        skip: !selectedProjectId,
    });

    const imageButtonRef = useCallback(
        (node: unknown) => {
            if (node !== null && open && trigerOpenFolderOnOpen === 'image') {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any
                (node as any).click();
            }
        },
        [open, trigerOpenFolderOnOpen],
    );
    const videoButtonRef = useCallback(
        (node: unknown) => {
            if (node !== null && open && trigerOpenFolderOnOpen === 'video') {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any
                (node as any).click();
            }
        },
        [open, trigerOpenFolderOnOpen],
    );

    const [updatePost] = useUpdatePostMutation();
    const [createPostMutation, { loading }] = useCreatePostMutation();

    const handleClose = () => {
        onClose();
    };

    const schema = Yup.object().shape(
        {
            id: Yup.string().nullable(),
            media: Yup.array()
                .of(
                    Yup.object().shape({
                        file: Yup.mixed().nullable(),
                        url: Yup.string().nullable(),
                    }),
                )
                .when('message', {
                    is: (message: string) => message === '',
                    then: () => Yup.array().required(t('common:errors:atLeastOneProperty')),
                })
                .compact((image: HybridMedium) => !image.file && !image.url),
            message: Yup.string()
                .nullable()
                .when('media', {
                    is: (media: CustionMedium[]) => (media as unknown[] | undefined)?.length === 0,
                    then: () => Yup.string().required(t('common:errors:atLeastOneProperty')),
                })
                .test({
                    name: 'maxLength',
                    message: t('common:errors.tooLong', { number: commonMultilineMaxLength }),
                    test: (message) => isMessageWithMentionsLengthValid(message, commonMultilineMaxLength),
                }),
        },
        [['message', 'media']],
    );

    const handleForm = async (values: FormValues) => {
        try {
            if (values.id) {
                await updatePost({
                    variables: {
                        postId: values.id,
                        message: values.message,
                        newsPostId: selectedNewsPostId as string,
                        adId: selectedAdId as string,
                        projectId: selectedProjectId as Project['id'],
                        media: values.media.map((medium) => ({
                            file: medium.file as unknown,
                            url: medium.file ? undefined : medium.url,
                            id: medium.file ? undefined : medium.id,
                        })) as HybridMedium[],
                    },
                    refetchQueries: ['Posts', 'NewsFeed', 'GalleryContent'],
                    awaitRefetchQueries: true,
                });
                addAlert({
                    type: 'success',
                    message: t('news:editPostSuccess'),
                });
            } else {
                await createPostMutation({
                    variables: {
                        message: values.message ?? '',
                        media: values.media.map(({ file }) => file),
                        newsPostId: selectedNewsPostId as string,
                        adId: selectedAdId as string,
                        projectId: selectedProjectId as Project['id'],
                    },
                    refetchQueries: ['Posts', 'NewsFeed'],
                    awaitRefetchQueries: true,
                });
                logEvent(AnalyticsEvent.CreatePost, {
                    POST_LENGTH: values.message?.length,
                    NUMBER_OF_PHOTOS: values.media.filter(({ file }) => file?.type.includes('image')).length,
                    NUMBER_OF_VIDEOS: values.media.filter(({ file }) => file?.type.includes('video')).length,
                });
                addAlert({
                    type: 'success',
                    message: t('news:postSent'),
                });
            }
            handleClose();
        } catch (error: unknown) {
            addAlert({
                type: 'error',
                message: (error as Error).message,
            });
        }
    };

    const disableLinkButton = ad !== undefined || newsPost !== undefined || project !== undefined;
    const companyOwnerOfPostCanCreateAd =
        post?.owner && isContentTypeManageable(ContentType.Ad, post.owner.company as Company);
    const companyOwnerOfPostCanCreateProject =
        post?.owner && isContentTypeManageable(ContentType.Project, post.owner.company as Company);

    const price = formatPrice({
        price: Number(ad?.ad?.price ?? ad?.ad?.rent),
        lang: i18n.language,
    });

    const linkMenuItems: EmotionJSX.Element[] = [];
    if (
        (companyOwnerOfPostCanCreateAd || post?.owner?.user || companyOwnerOfPostCanCreateProject) &&
        currentUser?.isRealEstatesTrades
    ) {
        linkMenuItems.push(
            <MenuItem key="SelectAdModalButton" onClick={openSelectAdModal} disabled={newsPost !== undefined}>
                <Icon name="RealEstateBuilding" size="1rem" />
                <Box ml={1}>
                    <Typography variant="h6">{t('news:linkAd')}</Typography>
                </Box>
            </MenuItem>,
        );
    }
    if (
        (companyOwnerOfPostCanCreateAd || post?.owner?.user || companyOwnerOfPostCanCreateProject) &&
        currentUser?.isCrafts
    ) {
        linkMenuItems.push(
            <MenuItem key="SelectProjectModalButton" onClick={openSelectProjectModal}>
                <Icon name="Projects" size="1rem" />
                <Box ml={1}>
                    <Typography variant="h6">{t('news:linkProject')}</Typography>
                </Box>
            </MenuItem>,
        );
    }
    linkMenuItems.push(
        <MenuItem key="SelectNewsPostModalButton" onClick={openSelectNewsPostModal} disabled={ad !== undefined}>
            <Icon name="Inactive" size="1rem" />
            <Box ml={1}>
                <Typography variant="h6">{t('news:linkNewsPost')}</Typography>
            </Box>
        </MenuItem>,
    );
    return post ? (
        <StyledDialog open={open} onClose={handleClose}>
            {loading ? (
                <Loader />
            ) : (
                <Formik
                    validationSchema={schema}
                    initialValues={{
                        id: post.id,
                        message: post.message ?? '',
                        media: post.media.map((medium) => ({
                            file: undefined,
                            ...medium,
                        })) as FormValues['media'],
                    }}
                    onSubmit={(values) => createAuthenticatedHandler(() => handleForm(values))}
                >
                    {(formikProps) => (
                        <Form data-target="dialog-post" data-target-id={`dialog-post-id-${post.id}`}>
                            <DialogTitle
                                onClose={() => {
                                    formikProps.resetForm();
                                    handleClose();
                                    setSelectedNewsPostId(post.newsPost?.id as string | null);
                                    setSelectedAdId(post.ad?.id as string | null);
                                    setSelectedProjectId(post.project?.id as Project['id'] | null);
                                }}
                                variant="h2"
                                data-target="dialog-post-edit-title"
                                data-target-id={`dialog-post-edit-title-id-${post.id}`}
                            >
                                {t('news:editPostTitle')}
                            </DialogTitle>
                            <DialogContent>
                                <DialogContentText component="div">
                                    <Box mt={-2}>
                                        <MentionTextField
                                            name="message"
                                            placeholder={t('news:whatDiscuss')}
                                            maxLength={commonMultilineMaxLength}
                                            rows={3}
                                            showCountDown
                                        />
                                    </Box>
                                </DialogContentText>
                                <Box display="flex">
                                    <StyledBox>
                                        <ImagePartPostModal
                                            name="media"
                                            media={formikProps.values.media}
                                            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
                                            propsFormikMedia={formikProps as any}
                                        />
                                        {(newsPost?.newsPost || ad?.ad || project?.project?.project) && (
                                            <Box mt={formikProps.values.media.length > 1 ? '0.5rem' : '0rem'}>
                                                <StyledTinyPreviewCard elevation={0}>
                                                    {ad?.ad && (
                                                        <TinyPreviewCard
                                                            iconName="AdIcon"
                                                            ad={ad.ad as Ad}
                                                            type="ad"
                                                            media={ad.ad.media as Medium[]}
                                                            subtitle={price}
                                                            title={t(`ad:propertyTypes.${ad.ad.propertyType}`)}
                                                        />
                                                    )}
                                                    {newsPost?.newsPost && (
                                                        <TinyPreviewCard
                                                            iconName="NewsIcon"
                                                            type="newsPost"
                                                            tag={newsPost.newsPost.featuredCategory as NewsCategory}
                                                            media={newsPost.newsPost.media as Medium[]}
                                                            subtitle={newsPost.newsPost.title as string}
                                                        />
                                                    )}
                                                    {project?.project?.project && (
                                                        <TinyPreviewCard
                                                            iconName="ProjectIcon"
                                                            title="news:project"
                                                            type="project"
                                                            media={project.project.project.media as Medium[]}
                                                            subtitle={project.project.project.name}
                                                        />
                                                    )}
                                                    <StyledIconCloseButton
                                                        onClick={() => {
                                                            setSelectedNewsPostId(null);
                                                            setSelectedAdId(null);
                                                            setSelectedProjectId(null);
                                                        }}
                                                    >
                                                        <Icon name="Delete" size="1rem" color={colours.primary.main} />
                                                    </StyledIconCloseButton>
                                                </StyledTinyPreviewCard>
                                            </Box>
                                        )}
                                        {formikProps.touched.media && formikProps.errors.media && (
                                            <Error>{formikProps.errors.media as React.ReactNode}</Error>
                                        )}
                                    </StyledBox>
                                </Box>
                            </DialogContent>
                            <DialogActions>
                                <Box display="flex" flexDirection="row" justifyContent="space-between" width="100%">
                                    <Box my={-1} mx={-1}>
                                        <CardActions>
                                            <PostFileInput type="image" inputRef={imageButtonRef} />
                                            <PostFileInput type="video" inputRef={videoButtonRef} />
                                            <Menu id="more-menu" items={linkMenuItems}>
                                                <StyledIconButton name="Link" disabled={disableLinkButton}>
                                                    <Icon
                                                        name="Link"
                                                        color={disableLinkButton ? 'grey' : 'black'}
                                                        size="1.25rem"
                                                    />
                                                </StyledIconButton>
                                            </Menu>
                                            {selectAdModalProps.open && post.owner && (
                                                <SelectAdModal
                                                    onSelection={setSelectedAdId}
                                                    userId={post.owner.user?.id}
                                                    companyId={post.owner.company?.id}
                                                    {...selectAdModalProps}
                                                />
                                            )}
                                            {selectProjectModalProps.open && post.owner && (
                                                <SelectProjectModal
                                                    onSelection={setSelectedProjectId}
                                                    userId={post.owner.user?.id}
                                                    companyId={post.owner.company?.id}
                                                    {...selectProjectModalProps}
                                                />
                                            )}
                                            {selectNewsPostModalProps.open && post.owner && (
                                                <SelectNewsPostModal
                                                    companyId={post.owner.company?.id}
                                                    onSelection={setSelectedNewsPostId}
                                                    {...selectNewsPostModalProps}
                                                />
                                            )}
                                        </CardActions>
                                    </Box>
                                    <Box display="flex" flexDirection="row">
                                        <Button
                                            variant="outlined"
                                            onClick={() => {
                                                formikProps.resetForm();
                                                handleClose();
                                                setSelectedNewsPostId(post.newsPost?.id as string | null);
                                                setSelectedAdId(post.ad?.id as string | null);
                                                setSelectedProjectId(post.project?.id as Project['id'] | null);
                                            }}
                                            data-target="btn-post-edit-cancel"
                                            data-target-id={`btn-post-edit-cancel-id-${post.id}`}
                                            style={{ marginRight: '1rem' }}
                                        >
                                            {t('common:modal.cancel')}
                                        </Button>
                                        <Button
                                            data-target="btn-post-edit-confirm"
                                            data-target-id={`btn-post-edit-confirm-id-${post.id}`}
                                            type="submit"
                                        >
                                            {t('news:editPostConfirmConfirm')}
                                        </Button>
                                    </Box>
                                </Box>
                            </DialogActions>
                        </Form>
                    )}
                </Formik>
            )}
        </StyledDialog>
    ) : null;
};

export default EditPostModal;
